import { useState, type ReactNode } from 'react'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { request } from '@/lib/api'
import { cn } from '@/lib/utils'
import { AreaState } from '../../../admin/shared/ui'
import { erpKeys, erpSettingsQuery } from '../../api'
import { matchesSearch } from '../../ui/calm-logic'
import { Pager, SearchBox, SegmentChips, usePager } from '../../ui/calm'
import { DetailPanel, HelpButton } from '../../ui/list-detail'
import { auditActionLabel, auditOk, CHANGE_KIND_LABELS, flattenHistory, type ChangeKind, type ChangeLine, type ErpSettingsChange } from '../settings-logic'
import { fmtDateTime, SettingsScreen } from '../ui'

/* Configurações › Segurança › Auditoria, em duas abas (pedido de 2026-11-03: a tela empilhava tudo e ficava poluída):
   - Ações no painel (abre aqui): toda ação do Staff (panel_audit, no cfb_db, schema panel; só admin). Período, busca e
     "só as que falharam" numa linha; as linhas agrupadas por dia (hora · ação · quem); só as que falharam ganham selo;
     o alvo e o código técnico ficam no detalhe ao clicar.
   - Mudanças nas Configurações: o histórico das Configurações do ERP (guardado com elas), no mesmo formato.
   O "como funciona" fica no (?) do título. */

type Tab = 'acoes' | 'erp'
type Period = '7' | '30' | '90' | '365'

type AuditLine = { at: string; email: string; action: string; target: string | null; result: string }
type AuditList = { source: 'database' | 'memory'; days: number; items: AuditLine[] }

/** Histórico; a chave leva o updatedAt das configurações, então cada gravação recarrega a lista. */
export const erpSettingsHistoryQuery = (updatedAt: string | null) => queryOptions({
    queryKey: [...erpKeys.settings, 'history', updatedAt] as const,
    queryFn: () => request<{ items: ErpSettingsChange[] }>('GET', '/admin/erp/settings/history'),
    staleTime: 15_000,
})

const auditQuery = (days: Period) => queryOptions({
    queryKey: [...erpKeys.settings, 'audit', days] as const,
    queryFn: () => request<AuditList>('GET', `/admin/audit?days=${days}`),
    staleTime: 15_000,
})

const PERIODS: { value: Period; label: string }[] = [
    { value: '7', label: 'Últimos 7 dias' }, { value: '30', label: 'Últimos 30 dias' }, { value: '90', label: 'Últimos 90 dias' }, { value: '365', label: 'Último ano' },
]

export function AuditSection() {
    const [tab, setTab] = useState<Tab>('acoes')
    const settings = useQuery(erpSettingsQuery)
    const history = useQuery(erpSettingsHistoryQuery(settings.data?.updatedAt ?? null))
    const changes = history.data ? flattenHistory(history.data.items) : []
    return (
        <SettingsScreen
            title="Auditoria"
            head={() => ({
                title: <span className="inline-flex items-center gap-1.5">Quem fez o quê no painel <HelpButton><Help /></HelpButton></span>,
                sub: 'Toda ação do Staff e cada mudança nas Configurações, sem dado pessoal.',
            })}
        >
            {() => (
                <div className="flex flex-col gap-3">
                    <SegmentChips<Tab> label="Ver" value={tab} onChange={setTab} options={[
                        { value: 'acoes', label: 'Ações no painel' },
                        { value: 'erp', label: 'Mudanças nas Configurações', count: history.data ? changes.length : undefined },
                    ]} />
                    {tab === 'acoes' ? <Actions /> : (
                        <AreaState query={history} noAccess={{ title: 'Só a Tecnologia vê o histórico', text: 'Peça a um administrador.' }}>
                            {() => <Changes lines={changes} />}
                        </AreaState>
                    )}
                </div>
            )}
        </SettingsScreen>
    )
}

function Help() {
    return (
        <>
            <p className="m-0"><b className="text-fg">Ações no painel</b>: cada busca, aprovação, exportação, “Mostrar” dado pessoal e mudança, com quem, quando e o resultado. Ficam 1 ano na tabela de auditoria (panel_audit, no cfb_db). Só administradores veem.</p>
            <p className="m-0"><b className="text-fg">Mudanças nas Configurações</b>: as 50 gravações mais recentes das Configurações do ERP, em frases curtas.</p>
            <p className="m-0">No servidor, também pelo comando <code>sessions.js audit --since AAAA-MM-DD</code>.</p>
        </>
    )
}

// ---------- Ações no painel ----------

function Actions() {
    const [days, setDays] = useState<Period>('30')
    const query = useQuery(auditQuery(days))
    return (
        <AreaState query={query} noAccess={{ title: 'Só a Tecnologia vê a auditoria', text: 'Peça a um administrador.' }}>
            {data => <ActionList key={days} data={data} days={days} onDays={setDays} />}
        </AreaState>
    )
}

function ActionList({ data, days, onDays }: { data: AuditList; days: Period; onDays: (d: Period) => void }) {
    const [q, setQ] = useState('')
    const [onlyFailed, setOnlyFailed] = useState(false)
    const [open, setOpen] = useState<AuditLine | null>(null)
    const found = data.items.filter(l => matchesSearch(q, l.email, l.action, auditActionLabel(l.action), l.target))
    const fails = found.filter(l => !auditOk(l.result)).length
    const shown = onlyFailed ? found.filter(l => !auditOk(l.result)) : found
    const page = usePager(shown.length)
    return (
        <div className="flex flex-col gap-2.5">
            <div className="flex flex-wrap items-center gap-2">
                <SearchBox value={q} onChange={v => { setQ(v); page.reset() }} placeholder="Pessoa, ação ou alvo" label="Buscar nas ações" />
                <label className="sr-only" htmlFor="audit-period">Período</label>
                <select id="audit-period" value={days} onChange={e => onDays(e.target.value as Period)} className="min-h-9 rounded-[10px] border border-line bg-panel px-2.5 text-[13.5px] text-fg">
                    {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <button
                    type="button"
                    aria-pressed={onlyFailed}
                    disabled={!fails && !onlyFailed}
                    onClick={() => { setOnlyFailed(v => !v); page.reset() }}
                    className={cn('min-h-9 cursor-pointer rounded-full border px-3 text-[13px] disabled:cursor-default disabled:opacity-50',
                        onlyFailed ? 'border-[var(--red-ink)] bg-[var(--red-bg)] text-[var(--red-ink)]' : 'border-line bg-panel text-muted hover:bg-[var(--hover)]')}
                >
                    Só as que falharam{fails ? ` · ${fails}` : ''}
                </button>
                <span className="ml-auto text-[12.5px] text-muted">{shown.length} {shown.length === 1 ? 'ação' : 'ações'}{data.source === 'memory' ? ' · só desta sessão' : ''}</span>
            </div>
            <DayGroups
                label="Ações no painel"
                items={shown.slice(page.start, page.end)}
                empty={data.items.length ? 'Nada com essa busca.' : 'Nenhuma ação nesse período.'}
                row={l => (
                    <Row
                        at={l.at}
                        title={auditActionLabel(l.action)}
                        meta={[l.email, cleanTarget(l.target)].filter(Boolean).join(' · ')}
                        failed={!auditOk(l.result)}
                        onOpen={() => setOpen(l)}
                    />
                )}
            />
            <Pager page={page} />
            {open && (
                <DetailPanel
                    title={auditActionLabel(open.action)}
                    sub={fmtDateTime(open.at)}
                    facts={[
                        { label: 'Quem', value: open.email },
                        { label: 'Resultado', value: auditOk(open.result) ? 'Deu certo' : `Falhou (${open.result})` },
                        { label: 'Alvo', value: cleanTarget(open.target) || '—' },
                        { label: 'Código da ação', value: <code className="text-[12.5px]">{open.action}</code> },
                    ]}
                    onClose={() => setOpen(null)}
                />
            )}
        </div>
    )
}

/** Tira o " · " solto do começo (busca sem perfil: " · 7 sugestões do agente"). */
const cleanTarget = (t: string | null) => (t ?? '').replace(/^[\s·]+/, '')

// ---------- Mudanças nas Configurações ----------

function Changes({ lines }: { lines: ChangeLine[] }) {
    const [q, setQ] = useState('')
    const [kind, setKind] = useState<'all' | ChangeKind>('all')
    const shown = lines.filter(l => (kind === 'all' || l.kind === kind) && matchesSearch(q, l.text, l.by))
    const page = usePager(shown.length)
    const kinds = (Object.keys(CHANGE_KIND_LABELS) as ChangeKind[]).filter(k => lines.some(l => l.kind === k))
    return (
        <div className="flex flex-col gap-2.5">
            {lines.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                    <SearchBox value={q} onChange={v => { setQ(v); page.reset() }} placeholder="Mudança ou quem fez" label="Buscar nas mudanças" />
                    {kinds.length > 1 && (
                        <>
                            <label className="sr-only" htmlFor="audit-kind">Tipo de mudança</label>
                            <select id="audit-kind" value={kind} onChange={e => { setKind(e.target.value as 'all' | ChangeKind); page.reset() }} className="min-h-9 rounded-[10px] border border-line bg-panel px-2.5 text-[13.5px] text-fg">
                                <option value="all">Todos os tipos</option>
                                {kinds.map(k => <option key={k} value={k}>{CHANGE_KIND_LABELS[k]}</option>)}
                            </select>
                        </>
                    )}
                </div>
            )}
            <DayGroups
                label="Mudanças nas Configurações do ERP"
                items={shown.slice(page.start, page.end)}
                empty={lines.length ? 'Nada com essa busca.' : 'Nenhuma mudança ainda: tudo está no padrão.'}
                row={l => <Row at={l.at} title={l.text} meta={`${l.by} · ${CHANGE_KIND_LABELS[l.kind]}`} />}
            />
            <Pager page={page} />
        </div>
    )
}

// ---------- lista por dia ----------

const dayKey = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
const timeOf = (iso: string) => new Date(iso).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })

function dayLabel(iso: string) {
    const d = dayKey(iso)
    if (d === dayKey(new Date().toISOString())) return 'Hoje'
    if (d === dayKey(new Date(Date.now() - 86_400_000).toISOString())) return 'Ontem'
    return d
}

/** As linhas da página em grupos por dia ("Hoje", "Ontem", "30/10/2026"). */
function DayGroups<T extends { at: string }>({ label, items, empty, row }: { label: string; items: T[]; empty: string; row: (item: T) => ReactNode }) {
    if (!items.length) return <p className="m-0 rounded-[14px] border border-line bg-panel px-4 py-3 text-[13.5px] text-muted">{empty}</p>
    const groups: { day: string; items: T[] }[] = []
    for (const it of items) {
        const day = dayLabel(it.at)
        const last = groups.at(-1)
        if (last?.day === day) last.items.push(it)
        else groups.push({ day, items: [it] })
    }
    return (
        <div aria-label={label} className="flex flex-col gap-2.5">
            {groups.map(g => (
                <section key={g.day} aria-label={g.day} className="flex flex-col">
                    <h3 className="m-0 px-1 pb-1 text-[12px] font-semibold tracking-wide text-muted uppercase">{g.day}</h3>
                    <ul className="m-0 list-none rounded-[14px] border border-line bg-panel p-1">
                        {g.items.map((it, i) => <li key={`${it.at}-${i}`} className="border-t border-line first:border-t-0">{row(it)}</li>)}
                    </ul>
                </section>
            ))}
        </div>
    )
}

function Row({ at, title, meta, failed = false, onOpen }: { at: string; title: string; meta: string; failed?: boolean; onOpen?: () => void }) {
    const body = (
        <>
            <time dateTime={at} className="tabular w-[44px] flex-none pt-px text-[12.5px] text-muted">{timeOf(at)}</time>
            <span className="flex min-w-0 flex-1 flex-col leading-[1.35]">
                <span className="truncate text-[13.5px] text-fg">{title}</span>
                <span className="truncate text-[12px] text-muted">{meta}</span>
            </span>
            {failed && <span className="flex-none self-center rounded-full bg-[var(--red-bg)] px-2 text-[11.5px] leading-[18px] text-[var(--red-ink)]">Falhou</span>}
        </>
    )
    const cls = 'flex w-full items-start gap-3 rounded-[10px] px-2.5 py-2 text-left'
    return onOpen
        ? <button type="button" onClick={onOpen} className={cn(cls, 'cursor-pointer border-0 bg-transparent font-[inherit] hover:bg-[var(--hover)]')}>{body}</button>
        : <div className={cls}>{body}</div>
}
